import * as React from "react";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import { KeyboardLayoutDesign } from "../../../store/LayoutData/types";
import "./index.css";

type LocalizationLanguage = "EN" | "DK" | "DE" | "PL" | "SE";

interface IState {
  layoutName: KeyboardLayoutType;
  input: string;
  multiFieldInputName: string;
}

interface IProps {
  onChange: (input: string) => void;
  keyboardRef: React.MutableRefObject<typeof Keyboard>;
  small?: boolean;
  medium?: boolean;
  layoutName?: KeyboardLayoutType;
  layoutDesign?: KeyboardLayoutDesign;
  input: string;
  multiFieldInputName?: string;
  language: LocalizationLanguage;
}

const layouts: Record<LocalizationLanguage, Record<string, string[]>> = {
  EN: {
    default: [
      "1 2 3 4 5 6 7 8 9 0 - = {bksp}",
      "q w e r t y u i o p [ ] \\",
      "a s d f g h j k l ; '",
      "{shift} z x c v b n m , . /",
      "@ {space}",
    ],
    shift: [
      "! @ # $ % ^ & * ( ) _ + {bksp}",
      "Q W E R T Y U I O P { } |",
      'A S D F G H J K L : "',
      "{shift} Z X C V B N M < > ?",
      "@ {space}",
    ],
    upperCaseAlphabet: [
      "1 2 3 4 5 6 7 8 9 0 - = {bksp}",
      "Q W E R T Y U I O P [ ] \\",
      "A S D F G H J K L ; '",
      "{shift} Z X C V B N M , . /",
      "@ {space}",
    ],
    numpad: ["1 2 3", "4 5 6", "7 8 9", "+ 0 {bksp}"],
  },
  DK: {
    default: [
      "1 2 3 4 5 6 7 8 9 0 + ´ {bksp}",
      "q w e r t y u i o p å ¨",
      "a s d f g h j k l æ ø '",
      "{shift} z x c v b n m , . -",
      "@ {space}",
    ],
    shift: [
      '! " # ¤ % & / ( ) = ? ` {bksp}',
      "Q W E R T Y U I O P Å ^",
      "A S D F G H J K L Æ Ø *",
      "{shift} Z X C V B N M ; : _",
      "@ {space}",
    ],
    upperCaseAlphabet: [
      "1 2 3 4 5 6 7 8 9 0 + ´ {bksp}",
      "Q W E R T Y U I O P Å ^",
      "A S D F G H J K L Æ Ø *",
      "{shift} Z X C V B N M ; : _",
      "@ {space}",
    ],
    numpad: ["1 2 3", "4 5 6", "7 8 9", "+ 0 {bksp}"],
  },
  SE: {
    default: [
      "1 2 3 4 5 6 7 8 9 0 + ´ {bksp}",
      "q w e r t y u i o p å ¨",
      "a s d f g h j k l ä ö '",
      "{shift} z x c v b n m , . -",
      "@ {space}",
    ],
    shift: [
      '! " # ¤ % & / ( ) = ? ` {bksp}',
      "Q W E R T Y U I O P Å ^",
      "A S D F G H J K L Ä Ö *",
      "{shift} Z X C V B N M ; : _",
      "@ {space}",
    ],
    upperCaseAlphabet: [
      "1 2 3 4 5 6 7 8 9 0 + ´ {bksp}",
      "Q W E R T Y U I O P Å ^",
      "A S D F G H J K L Ä Ö *",
      "{shift} Z X C V B N M ; : _",
      "@ {space}",
    ],
    numpad: ["1 2 3", "4 5 6", "7 8 9", "+ 0 {bksp}"],
  },
  DE: {
    default: [
      "1 2 3 4 5 6 7 8 9 0 ß ´ {bksp}",
      "q w e r t z u i o p ü +",
      "a s d f g h j k l ö ä #",
      "{shift} y x c v b n m , . -",
      "@ {space}",
    ],
    shift: [
      '! " § $ % & / ( ) = ? ` {bksp}',
      "Q W E R T Z U I O P Ü *",
      "A S D F G H J K L Ö Ä '",
      "{shift} Y X C V B N M ; : _",
      "@ {space}",
    ],
    upperCaseAlphabet: [
      "1 2 3 4 5 6 7 8 9 0 ß ´ {bksp}",
      "Q W E R T Z U I O P Ü +",
      "A S D F G H J K L Ö Ä #",
      "{shift} Y X C V B N M , . -",
      "@ {space}",
    ],
    numpad: ["1 2 3", "4 5 6", "7 8 9", "+ 0 {bksp}"],
  },
  PL: {
    default: [
      "1 2 3 4 5 6 7 8 9 0 - = {bksp}",
      "q w e r t y u i o p [ ]",
      "a s d f g h j k l ; ' \\",
      "{shift} z x c v b n m , . /",
      "@ {space}",
    ],
    shift: [
      "! @ # $ % ^ & * ( ) _ + {bksp}",
      "Q W E R T Y U I O P { } |",
      'A S D F G H J K L : "',
      "{shift} Z X C V B N M < > ?",
      "@ {space}",
    ],
    upperCaseAlphabet: [
      "1 2 3 4 5 6 7 8 9 0 - = {bksp}",
      "Q W E R T Y U I O P [ ]",
      "A S D F G H J K L ; '",
      "{shift} Z X C V B N M , . /",
      "@ {space}",
    ],
    numpad: ["1 2 3", "4 5 6", "7 8 9", "+ 0 {bksp}"],
  },
};

class KeyboardWrapper extends React.Component<IProps, IState> {
  public readonly state: IState = {
    layoutName: "upperCaseAlphabet",
    input: "",
    multiFieldInputName: "",
  };

  componentDidMount() {
    this.setState({ layoutName: this.props.layoutName ?? "upperCaseAlphabet" });
  }

  // Update internal state when props change
  componentDidUpdate(prevProps: IProps) {
    const { layoutName, input, multiFieldInputName } = this.props;

    if (
      multiFieldInputName &&
      prevProps.multiFieldInputName !== multiFieldInputName
    ) {
      this.setState({ multiFieldInputName });
    }

    if (prevProps.layoutName !== layoutName) {
      this.setState({ layoutName });
    }

    if (prevProps.input !== input) {
      let newLayoutName = this.state.layoutName;

      if (layoutName !== "numpad") {
        if (input.length === 0 || input.endsWith(" ")) {
          newLayoutName = "upperCaseAlphabet";
        } else if (input.length > 0 && !input.endsWith(" ")) {
          newLayoutName = "default";
        }
      }

      if (newLayoutName !== this.state.layoutName) {
        this.setState({ layoutName: newLayoutName });
      }

      this.setState({ input });
    }
  }

  private onKeyPress = (button: string) => {
    const { layoutName } = this.state;
    if (button === "{shift}") {
      let newLayout: KeyboardLayoutType =
        layoutName === "default" ? "shift" : "default";
      this.setState({ layoutName: newLayout });
    }
  };

  render() {
    const { onChange, keyboardRef, small, medium, language } = this.props;
    let className = medium ? "medium" : small ? "small" : "big";

    const display = {
      "{bksp}": "⌫",
      "{enter}": "↵",
      "{shift}": "⇧",
      "{space}": "Space",
    };
    switch (this.props.layoutDesign) {
      case KeyboardLayoutDesign.SMALL:
        className = "small";
        break;
      case KeyboardLayoutDesign.MEDIUM:
        className = "medium";
        break;
      case KeyboardLayoutDesign.BIG:
        className = "big";
        break;
      case KeyboardLayoutDesign.NUMPAD:
        className = "numpad";
        break;
      default:
    }

    return (
      <div className={className}>
        <Keyboard
          keyboardRef={(r: any) => (keyboardRef.current = r)}
          layoutName={this.state.layoutName}
          onChange={onChange}
          onKeyPress={this.onKeyPress}
          display={display}
          mergeDisplay={true}
          inputName={this.props.multiFieldInputName}
          layout={layouts[language]}
        />
      </div>
    );
  }
}

export default KeyboardWrapper;
